/**
 * xp1_LandingIndex - Landing Page Experiments Hub
 *
 * Central page to preview and compare all landing page concepts.
 * Accessible from DevPage for owner testing.
 */

import { Link } from 'react-router-dom';
import './xp1_LandingIndex.css';

// ACTIVE - Depth Cinema winners (clickable)
const WINNERS = [
  {
    id: 'xp1h-d3',
    name: 'Depth Cinema V3-HD [WINNER]',
    description: 'FINAL production landing page. Full effects, maximum visual impact.',
    features: ['backdrop-filter: blur(20px)', 'will-change on hero', 'Full 20px blur reflections', 'Non-throttled scroll', 'Maximum visual fidelity'],
    color: '#f7768e',
  },
  {
    id: 'xp1h-d2',
    name: 'Depth Cinema V2 [OPTIMIZED]',
    description: 'Performance-optimized version with testimonials and responsive device frame.',
    features: ['Smooth scroll (rAF throttled)', 'No backdrop-filter', 'Testimonials section', 'Responsive device frame', 'Staggered animations'],
    color: '#ff9e64',
  },
  {
    id: 'xp1h-d',
    name: 'Depth Cinema V1 [ORIGINAL]',
    description: 'Original winner. Cinematic Hero + Immersive Depth + Neon Cinema.',
    features: ['3D perspective hero', 'Depth cards on scroll', 'Central stream line', 'Neon glow effects', 'Cinematic transitions'],
    color: '#9ece6a',
  },
];

// FEATURE PAGE - Winner is now live at /features
const FEATURE_PAGE = {
  id: 'features',
  name: 'Features Page [LIVE]',
  description: 'Bento Grid layout - the winner. Now live at /features.',
  features: ['Bento grid cards', 'Hero stats', 'PWA install info', 'Pricing preview'],
  color: '#9ece6a',
};

// ARCHIVED - Source files removed, links no longer work
// Archived to: /ninjacash/archived_showcases.tar.gz (2026-01-19)
const ARCHIVED = [
  { id: 'xp1-a', name: 'Concept A: Cinematic Hero', color: '#7aa2f7' },
  { id: 'xp1-b', name: 'Concept B: Interactive Carousel', color: '#9ece6a' },
  { id: 'xp1-c', name: 'Concept C: Scroll Storytelling', color: '#7dcfff' },
  { id: 'xp2-a', name: 'Concept A2: Brutalist Grid', color: '#7aa2f7' },
  { id: 'xp2-b', name: 'Concept B2: Soft Glass', color: '#bb9af7' },
  { id: 'xp2-c', name: 'Concept C2: Editorial Flow', color: '#9ece6a' },
  { id: 'xp3-a', name: 'Concept A3: Immersive Depth', color: '#7aa2f7' },
  { id: 'xp3-b', name: 'Concept B3: Dynamic Bento', color: '#bb9af7' },
  { id: 'xp3-c', name: 'Concept C3: Neon Stream', color: '#7dcfff' },
  { id: 'xp3h-a', name: 'Hybrid A: Glass Flow', color: '#bb9af7' },
  { id: 'xp3h-b', name: 'Hybrid B: Cyber Grid', color: '#00ff94' },
  { id: 'xp3h-c', name: 'Hybrid C: Story Slider', color: '#f43f5e' },
  { id: 'xp3h-d', name: 'Hybrid D: Neon Cinema', color: '#00f3ff' },
  { id: 'xp1h-a', name: 'XP1 Hybrid A: Aurora Dashboard', color: '#7aa2f7' },
  { id: 'xp1h-b', name: 'XP1 Hybrid B: Terminal Scroll', color: '#9ece6a' },
  { id: 'xp1h-c', name: 'XP1 Hybrid C: Magazine Carousel', color: '#bb9af7' },
];

export default function xp1_LandingIndex() {
  return (
    <div className="xp1-index">
      <header className="xp1-index-header">
        <h1 className="xp1-index-title">Landing Page Experiments</h1>
        <p className="xp1-index-subtitle">
          Depth Cinema won! V3-HD is the production landing page.
        </p>
      </header>

      {/* WINNERS - Active and clickable */}
      <h2 style={{ color: '#9ece6a', margin: '2rem 0 1rem', fontSize: '1.5rem' }}>
        Winners - Depth Cinema
      </h2>
      <div className="xp1-index-grid">
        {WINNERS.map((concept) => (
          <div
            key={concept.id}
            className="xp1-concept-card"
            style={{ '--accent': concept.color } as React.CSSProperties}
          >
            <div className="xp1-concept-header">
              <span className="xp1-concept-id">{concept.id}</span>
              <h2 className="xp1-concept-name">{concept.name}</h2>
            </div>
            
            <p className="xp1-concept-desc">{concept.description}</p>
            
            <ul className="xp1-concept-features">
              {concept.features.map((feature, i) => (
                <li key={i}>{feature}</li>
              ))}
            </ul>

            <div className="xp1-concept-actions">
              <Link to={`/${concept.id}`} className="xp1-concept-btn primary">
                View Full Page
              </Link>
              <a
                href={`/${concept.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="xp1-concept-btn secondary"
              >
                Open in New Tab
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* FEATURE PAGE - Live */}
      <h2 style={{ color: '#9ece6a', margin: '3rem 0 1rem', fontSize: '1.5rem' }}>
        Features Page (LIVE)
      </h2>
      <p style={{ color: '#787f9a', fontSize: '0.9rem', marginBottom: '1rem' }}>
        Bento Grid won! Now live at /features. Archived: features-b, features-c (gzipped).
      </p>
      <div className="xp1-index-grid" style={{ maxWidth: '400px' }}>
        <div
          className="xp1-concept-card"
          style={{ '--accent': FEATURE_PAGE.color } as React.CSSProperties}
        >
          <div className="xp1-concept-header">
            <span className="xp1-concept-id">{FEATURE_PAGE.id}</span>
            <h2 className="xp1-concept-name">{FEATURE_PAGE.name}</h2>
          </div>
          
          <p className="xp1-concept-desc">{FEATURE_PAGE.description}</p>
          
          <ul className="xp1-concept-features">
            {FEATURE_PAGE.features.map((feature, i) => (
              <li key={i}>{feature}</li>
            ))}
          </ul>

          <div className="xp1-concept-actions">
            <Link to="/features" className="xp1-concept-btn primary">
              View Live Page
            </Link>
          </div>
        </div>
      </div>

      {/* ARCHIVED - Not accessible, for reference only */}
      <h2 style={{ color: '#787f9a', margin: '3rem 0 1rem', fontSize: '1.25rem' }}>
        Archived Experiments (not accessible)
      </h2>
      <p style={{ color: '#565f89', fontSize: '0.875rem', marginBottom: '1rem' }}>
        Source files archived to <code>archived_showcases.tar.gz</code> (2026-01-19)
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        {ARCHIVED.map((item) => (
          <span
            key={item.id}
            style={{
              padding: '0.375rem 0.75rem',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '0.25rem',
              fontSize: '0.8rem',
              color: '#787f9a',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
            title={item.name}
          >
            {item.id}
          </span>
        ))}
      </div>

      <footer className="xp1-index-footer" style={{ marginTop: '3rem' }}>
        <div className="xp1-index-notes">
          <h3>Notes</h3>
          <ul>
            <li>V3-HD is the production landing page (copied to LoginPage.tsx)</li>
            <li>Archived experiments available in <code>archived_showcases.tar.gz</code></li>
            <li>All winners use screenshots from <code>/showcase/</code></li>
          </ul>
        </div>
        <div className="xp1-index-links">
          <h3>Quick Links</h3>
          <div className="xp1-quick-links">
            <Link to="/xp1h-d3">v3-HD [WINNER]</Link>
            <Link to="/xp1h-d2">v2-opt</Link>
            <Link to="/xp1h-d">v1-orig</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
